import React, { createContext, useContext, useState, useCallback, useRef } from "react";

const NotificationsContext = createContext(null);

let id = 0;

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const recentNotificationsRef = useRef({});

  const notify = useCallback((type, message, timeout = 4500) => {
    // normalize message to object for structured rendering
    const payload = typeof message === "string" ? { text: message } : message;
    const dedupeKey = JSON.stringify({
      type,
      title: payload?.title || "",
      text: payload?.text || "",
      errors: payload?.errors || [],
    });
    const now = Date.now();
    const lastShownAt = recentNotificationsRef.current[dedupeKey] || 0;

    if (now - lastShownAt < 3000) return;

    recentNotificationsRef.current[dedupeKey] = now;
    const nid = ++id;

    setNotifications((s) => [...s, { id: nid, type, message: payload }]);
    if (timeout > 0) {
      setTimeout(() => {
        setNotifications((s) => s.filter((n) => n.id !== nid));
      }, timeout);
    }
  }, []);

  const remove = useCallback((nid) => {
    setNotifications((s) => s.filter((n) => n.id !== nid));
  }, []);

  return (
    <NotificationsContext.Provider value={{ notify, remove }}>
      {children}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
        {notifications.map((n) => (
          <div
            key={n.id}
            // increased max width and reduced vertical padding; enable wrapping and limit height with scroll if too long
            className={`max-w-xl w-full text-white px-6 py-1 rounded shadow-md border-l-4 break-words whitespace-normal overflow-auto max-h-48 ${
              n.type === "success"
                ? "bg-green-600 border-green-800"
                : n.type === "error"
                ? "bg-red-600 border-red-800"
                : "bg-blue-600 border-blue-800"
            }`}
            style={{ wordBreak: "break-word" }}
          >
            {n.message?.title && (
              <div className="font-semibold text-sm">{n.message.title}</div>
            )}
            {n.message?.text && <div className="text-sm">{n.message.text}</div>}
            {n.message?.errors && Array.isArray(n.message.errors) && (
              <ul className="mt-1 list-disc list-inside text-sm">
                {n.message.errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            )}
            <button
              onClick={() => remove(n.id)}
              className="ml-2 float-right opacity-80 hover:opacity-100 text-xl leading-none"
              aria-label="close"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </NotificationsContext.Provider>
  );
};

export const useNotification = () => {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error("useNotification must be used within NotificationProvider");
  return ctx.notify;
};

export default NotificationProvider;
